import {
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  untrack,
  useContext,
  type Accessor,
  type JSX,
} from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { useApi, isOk } from "../api";
import type { BotMsgContactDto } from "../api";
import { useActiveBot } from "./activeBot";
import { useUserContext } from "../components/UserContextProvider";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useVisibilityPoll } from "../hooks/useVisibilityPoll";
import { subscribeBotEngineEvents, subscribeBotEngineStream } from "../services/botEngineEvents";
import { debounce } from "../services/debounceCallback";

interface MessageUnreadState {
  contacts: Accessor<readonly BotMsgContactDto[]>;
  loadingContacts: Accessor<boolean>;
  unreadCount: Accessor<number>;
  isUnread: (c: BotMsgContactDto) => boolean;
  reload: () => Promise<void>;
  markRead: (contactName: string) => Promise<void>;
}

const MessageUnreadContext = createContext<MessageUnreadState>();

// Every dashboard tab mounts this provider, so the contact list follows the event stream and polls
// only as a safety net; a 5s poll from every open tab was the msgs endpoints' main load.
const FALLBACK_POLL_MS = 30_000;
const EVENT_RELOAD_DEBOUNCE_MS = 400;
const EVENT_RELOAD_MAX_WAIT_MS = 2000;

export function MessageUnreadProvider(props: { children: JSX.Element }) {
  const api = useApi();
  const { user } = useUserContext();
  const { botId } = useActiveBot();
  const msgsEnabled = useFeatureFlag("MSGS");

  const [contacts, setContacts] = createStore<BotMsgContactDto[]>([]);
  const [loading, setLoading] = createSignal(true);

  const canPoll = () => !!user()?.loggedin && !!botId() && msgsEnabled();

  const reload = async () => {
    const id = botId();
    if (!id || !user()?.loggedin || !msgsEnabled()) {
      setContacts(reconcile([], { key: "name" }));
      setLoading(false);
      return;
    }
    const res = await api.botMsg.getContacts(id);
    if (isOk(res.data)) setContacts(reconcile(res.data.value.contacts ?? [], { key: "name" }));
    setLoading(false);
  };

  const markRead = async (name: string) => {
    const id = botId();
    if (!id) return;
    const idx = contacts.findIndex((c) => c.name.toLowerCase() === name.toLowerCase());
    if (idx >= 0 && contacts[idx].unreadCount > 0) setContacts(idx, "unreadCount", 0);
    await api.botMsg.markRead(id, { contact: name });
    reloadSoon();
  };

  const reloadSoon = debounce(() => void reload(), EVENT_RELOAD_DEBOUNCE_MS, EVENT_RELOAD_MAX_WAIT_MS);
  onCleanup(() => reloadSoon.cancel());

  const unreadCount = () => contacts.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
  const isUnread = (c: BotMsgContactDto) => (c.unreadCount ?? 0) > 0;

  createEffect(() => {
    const ready = !!user()?.loggedin && msgsEnabled();
    const id = botId();
    setLoading(true);
    if (ready && id) {
      untrack(() => void reload());
    } else {
      setContacts(reconcile([], { key: "name" }));
      setLoading(false);
    }
  });

  createEffect(() => {
    if (!canPoll()) return;
    const id = botId()!;
    const stopEvents = subscribeBotEngineEvents(id, (evt) => {
      if (evt.type === "msg_received") reloadSoon();
    });
    let seenEpoch: number | null = null;
    const stopStream = subscribeBotEngineStream(id, (stream) => {
      if (seenEpoch !== null && stream.epoch !== seenEpoch) reloadSoon();
      seenEpoch = stream.epoch;
    });
    onCleanup(() => { stopEvents(); stopStream(); });
  });

  onMount(() => {
    useVisibilityPoll(() => { if (canPoll()) void reload(); }, FALLBACK_POLL_MS);
  });

  const state: MessageUnreadState = {
    contacts: () => contacts,
    loadingContacts: loading,
    unreadCount,
    isUnread,
    reload,
    markRead,
  };

  return <MessageUnreadContext.Provider value={state}>{props.children}</MessageUnreadContext.Provider>;
}

export function useMessageUnread(): MessageUnreadState {
  const ctx = useContext(MessageUnreadContext);
  if (!ctx) throw new Error("useMessageUnread must be used within MessageUnreadProvider");
  return ctx;
}
